// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { canRmaOrder } from "@insite/client-framework/Store/Pages/OrderDetails/OrderDetailsSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import AskQuestionModal from "@pacific/App/Components/AskQuestionModal/AskQuestionModal";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@pacific/XDL/constants";
import { XngOrderModel } from "@pacific/XDL/types";
import { ellipsis, getFormattedDate, getOrderNumber, xScreen } from "@pacific/XDL/Utilities";
import { FC } from "react";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    // XNG-Change
    order: XngOrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);

    return {
        language: state.context.session.language,
        isReordering: state.pages.orderHistory.isReordering,
        allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        allowRma: canRmaOrder(state, ownProps.order),
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
    };
};

const mapDispatchToProps = {
    reorder,
    setAddToListModalIsOpen,
    addToWishList,
    loadOrderStatusMappings,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> & {
        setAddToListModalIsOpen: (parameter: {
            productInfos?: Omit<ProductInfo, "productDetailPath">[];
            modalIsOpen: boolean;
        }) => void;
        addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    } & OwnProps &
    HasToasterContext;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    shipToCell: {
        css: css`
            ${xScreen("gt", "md")} {
                max-width: 300px;
                ${ellipsis(2)}
                height: 60px;
                white-space: pre-wrap;
            }
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    customerPOCell: {
        css: css`
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
            }
        `,
    },
};
const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    isReordering,
    toaster,
    reorder,
    allowRma,
    rmaLink,
    setAddToListModalIsOpen,
    addToWishList,
    allowMultipleWishLists,
    shouldLoadOrderStatusMappings,
    loadOrderStatusMappings,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes
    const history = useHistory();
    const [action, setSelectedAction] = useState("");

    React.useEffect(() => {
        if (shouldLoadOrderStatusMappings) {
            loadOrderStatusMappings();
        }
    }, []);

    const actionChangeHandler = (actionName: string) => {
        setSelectedAction(actionName);
        const orderNumber = getOrderNumber(order.erpOrderNumber, order.webOrderNumber);
        switch (actionName) {
            case "reorder":
                reorderHandler(orderNumber);
                break;
            case "addAllToList":
                addToListHandler();
                break;
            case "askQuestion":
                askQuestionHandler();
                break;
            case "returnRequest":
                returnRequestHandler();
                break;
        }
    };

    const returnRequestHandler = () => {
        const orderNumber = getOrderNumber(order.erpOrderNumber, order.webOrderNumber);
        rmaLink && history.push(`${rmaLink.url}?orderNumber=${orderNumber}`);
    };

    const askQuestionHandler = () => {
        setIsModalOpen(true);
    };

    const closeAskQuestionModal = () => {
        setIsModalOpen(false);
        setSelectedAction("");
    };

    const addToListHandler = () => {
        const productInfos =
            order.orderLines
                ?.filter(orderLine => orderLine.canAddToWishlist)
                .map(orderLine => {
                    return {
                        productId: orderLine.productId!,
                        qtyOrdered: orderLine.qtyOrdered!,
                        unitOfMeasure: orderLine.unitOfMeasure ?? "",
                    };
                }) || [];

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                    setSelectedAction("");
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
        setTimeout(() => {
            setSelectedAction("");
        }, 1000);
    };

    const reorderHandler = (orderNumber: string) => {
        reorder({
            orderNumber,
            onSuccess: () => {
                toaster.addToast({
                    body: (
                        <>
                            <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />
                            <StyledWrapper as="span">&nbsp;{translate("Added to Cart")}</StyledWrapper>
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
                setSelectedAction("");
            },
        });
    };

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            ...sharedDateTimeOptions,
        },
    });

    // XNG-Change
    const requestedDeliveryDisplay = order.requestedDeliveryDateDisplay
        ? getFormattedDate(new Date(order.requestedDeliveryDateDisplay), true)
        : null;

    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const [isModalOpen, setIsModalOpen] = useState(false);
    // XNG-Change
    const shipToOrPickupLocation =
        order.fulfillmentMethod === FulfillmentMethod.PickUp
            ? `${order?.pickupWarehouse?.description} ${order?.pickupWarehouse?.address1} ${order?.pickupWarehouse?.address2} ${order?.pickupWarehouse?.city} ${order?.pickupWarehouse?.state}`
            : `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}` ||
              EMPTY_VALUE;

    return (
        <>
            <DataTableCell {...styles.dataCell}>{orderDateDisplay && orderDateDisplay}</DataTableCell>
            <DataTableCell>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>
                {/* XNG-Change */}
                {shipToOrPickupLocation}
            </DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{`${order.customerPO || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${requestedDeliveryDisplay || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.orderGrandTotalDisplay || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell>
                <Select
                    value={action}
                    onChange={event => actionChangeHandler(event.target.value)}
                    data-test-selector="actions"
                >
                    <option value="">{translate("Select")}</option>
                    <option value="reorder" disabled={Object.keys(isReordering).length > 0}>
                        {translate("Reorder")}
                    </option>
                    <option value="addAllToList">{translate("Add All to List")}</option>
                    <option value="askQuestion">{translate("Ask A Question")}</option>
                    {allowRma && <option value="returnRequest">{translate("Return Request")}</option>}
                </Select>
            </DataTableCell>
            <AskQuestionModal isOpen={isModalOpen} order={order} onClose={closeAskQuestionModal} />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(OrderSummaryCard));
